import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMR, UMSelect } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { YEARS } from './engine';
import { routeOf } from './construction';
import { MapPane, type MapOverlay } from './MapPane';
import { ContextSets } from './ContextSets';
import type { CalculationSection } from './sectionEngine';
import { PhaseTable } from './PhaseTable';
import { PipePhaseTable } from './PipePhaseTable';
import type { Family, Plan, Project } from './types';

export function PhasingWorkspace({ project, plan, geo, canWrite, dirty, onUpdate, onOpenSet, onRun }: {
  project: Project; plan: Plan; geo?: PlanGeoData; canWrite: boolean; dirty: boolean; onUpdate: (changes: Partial<Plan>) => void;
  onOpenSet: (family: Family, id: string) => void; onRun: (section: CalculationSection) => void;
}) {
  const [target, setTarget] = useState<'buildings' | 'pipes'>('buildings');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPipes, setSelectedPipes] = useState<number[]>([]);
  const [focusId, setFocusId] = useState('');
  const [focusRequest, setFocusRequest] = useState(0);
  const [year, setYear] = useState(YEARS[0]);
  const [area, setArea] = useState(false);
  const [message, setMessage] = useState('');
  const [resetConfirmation, setResetConfirmation] = useState(false);
  const [autoConfirmation, setAutoConfirmation] = useState(false);
  const route = routeOf(plan, geo?.pipes.features.length ?? 0);
  const validIds = selectedIds.filter((id) => plan.buildingIds?.includes(id));
  const validPipes = selectedPipes.filter((index) => route.indexes.includes(index));
  const overlay: MapOverlay = {
    selectedBuildings: plan.buildingIds,
    focusedBuildingId: focusId || undefined,
    focusRequest,
    highlightedBuildings: validIds,
    highlightedPipes: validPipes,
    buildingPhases: Object.fromEntries((plan.buildingIds ?? []).map((id) => [id, plan.buildingPhases?.[id] ?? YEARS[0]])),
    routePipeIndexes: route.indexes, routePipePhases: route.phases,
    technicalPoints: project.technicalPoints?.filter((point) => point.status === 'validated'),
    areaMode: area && canWrite,
    onAreaError: setMessage,
    onAreaSelect: (selection) => {
      setArea(false);
      if (target === 'buildings') {
        const ids = selection.buildings.filter((id) => plan.buildingIds?.includes(id));
        setSelectedIds(ids);
        setMessage(`${ids.length} bâtiments du plan dans la zone. Choisissez une année puis appliquez.`);
      } else {
        const indexes = selection.pipes.filter((index) => route.indexes.includes(index));
        setSelectedPipes(indexes);
        setMessage(`${indexes.length} canalisations du tracé dans la zone. Choisissez une année puis appliquez.`);
      }
    },
    onBuildingClick: target === 'buildings' ? (id) => {
      if (!plan.buildingIds?.includes(id)) { setMessage('Ce bâtiment ne fait pas partie du plan. Ajoutez-le dans Bâtiments pour le phaser.'); return; }
      setFocusId(id); setFocusRequest((value) => value + 1); setSelectedIds([id]); setMessage('Bâtiment retrouvé dans le tableau. Le clic seul ne change pas son année.');
    } : undefined,
    onPipeClick: target === 'pipes' ? (index) => {
      setSelectedPipes((indexes) => indexes.includes(index) ? indexes.filter((item) => item !== index) : [...indexes, index]);
    } : undefined,
    legend: [
      area ? 'Lasso : maintenez le bouton gauche et dessinez une zone.' : 'Cliquez un bâtiment ou une canalisation selon le mode choisi.',
      '2027 : orange · 2028 : vert · 2029 : bleu (cycle de couleurs de 3 ans).',
      'Rose : éléments sélectionnés. Les dates restent modifiables dans le tableau.',
    ],
  };
  return <div style={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
    <section style={{ flex: '0 0 55%', minWidth: 0, overflowY: 'auto', padding: 16, borderRight: `1px solid ${UMR.border}` }}>
      <UMFormStyles />
      <NJText as="h3" scale="md">Phasage · tableau et carte</NJText>
      <ContextSets project={project} plan={plan} families={['developpement', 'phasage']} canWrite={canWrite} onUpdate={onUpdate} onOpenSet={onOpenSet} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <NJButton label="Bâtiments" scale="sm" variant={target === 'buildings' ? 'primary' : 'secondary'} onClick={() => { setTarget('buildings'); setArea(false); }} />
        <NJButton label="Canalisations" scale="sm" variant={target === 'pipes' ? 'primary' : 'secondary'} onClick={() => { setTarget('pipes'); setArea(false); }} />
        <NJButton label="Phasage auto — simulation" scale="sm" variant="secondary" disabled={!canWrite || dirty || !plan.traceCalculated}
          onClick={() => setAutoConfirmation(true)} />
      </div>
      <NJText scale="xs">Simulation : ordre des bâtiments, cadence Phasage, horizon Développement. Les dates restent modifiables ; aucun moteur métier. Confirmez le brouillon avant un calcul local.</NJText>
      {autoConfirmation && <section role="alert">
        <NJText scale="xs">Le phasage auto remplacera les dates des bâtiments. Les dates manuelles des canalisations seront conservées.</NJText>
        <NJButton label="Confirmer le phasage auto simulé" scale="sm" disabled={!canWrite || dirty} onClick={() => { onRun('phasing'); setAutoConfirmation(false); }} />
        <NJButton label="Annuler le phasage auto" variant="secondary" scale="sm" onClick={() => setAutoConfirmation(false)} />
      </section>}
      {canWrite && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <NJButton label={area ? 'Annuler le lasso' : 'Sélectionner par lasso'} variant={area ? 'primary' : 'secondary'} scale="sm" onClick={() => setArea(!area)} />
        <UMSelect value={year} ariaLabel="Année du groupe" options={YEARS.map((value) => ({ value, label: value }))} onChange={setYear} />
        <NJButton label={`Appliquer ${year} aux ${target === 'buildings' ? validIds.length : validPipes.length} éléments`}
          scale="sm" variant="primary" disabled={target === 'buildings' ? !validIds.length : !validPipes.length} onClick={() => {
            if (target === 'buildings') onUpdate({ buildingPhases: { ...plan.buildingPhases, ...Object.fromEntries(validIds.map((id) => [id, year])) } });
            else onUpdate({ pipePhaseOverrides: { ...plan.pipePhaseOverrides, ...Object.fromEntries(validPipes.map((index) => [index, year])) } });
            setMessage(`Année ${year} appliquée au brouillon ; confirmez les modifications.`);
          }} />
      </div>}
      {message && <NJText scale="xs" role="status">{message}</NJText>}
      {target === 'buildings' ? <PhaseTable plan={plan} canWrite={canWrite} focusId={focusId} focusRequest={focusRequest}
        selectedIds={validIds} onSelect={setSelectedIds} onFocus={(id) => { setFocusId(id); setFocusRequest((value) => value + 1); setSelectedIds([id]); }}
        onYear={(id, next) => onUpdate({ buildingPhases: { ...plan.buildingPhases, [id]: next } })} />
        : <section>
          <NJText scale="xs">Automatique (mock) : phase la plus précoce de deux bâtiments associés par ordre de liste. Une date manuelle est prioritaire ; aucun écrasement automatique.</NJText>
          {canWrite && <NJButton label="Phaser les canalisations d’après les bâtiments" scale="sm" variant="secondary"
            disabled={!validPipes.length} onClick={() => setResetConfirmation(true)} />}
          {resetConfirmation && target === 'pipes' && <div role="alert" style={{ border: `1px solid ${UMR.border}`, padding: 12 }}>
            <NJText scale="sm">Supprimer les dates manuelles des {validPipes.length} canalisations sélectionnées ?</NJText>
            <NJButton label="Confirmer le phasage automatique" scale="sm" disabled={!canWrite} onClick={() => {
              const overrides = { ...plan.pipePhaseOverrides };
              validPipes.forEach((index) => { delete overrides[index]; });
              onUpdate({ pipePhaseOverrides: overrides }); setResetConfirmation(false);
            }} />
            <NJButton label="Conserver les dates manuelles" scale="sm" variant="secondary" onClick={() => setResetConfirmation(false)} />
          </div>}
          <PipePhaseTable plan={plan} geo={geo} canWrite={canWrite} selected={validPipes} onSelect={setSelectedPipes}
            onYear={(index, next) => onUpdate({ pipePhaseOverrides: { ...plan.pipePhaseOverrides, [index]: next } })} />
        </section>}
      <NJText scale="xs">Imports et exports CSV : section Import-export du projet. Les dates automatiques se propagent aux canalisations sans date manuelle ; sélectionnez des canalisations pour remplacer explicitement leurs dates manuelles.</NJText>
    </section>
    {geo ? <MapPane geo={geo} overlay={overlay} /> : <NJText role="status">Chargement de la carte…</NJText>}
  </div>;
}
